/**
 * Camunda 7.18 REST API Client
 * Base HTTP client with authentication and error handling
 */

import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface CamundaApiConfig {
  baseUrl?: string;
  timeout?: number;
  authStorageKey?: string;
}

export interface CamundaApiError {
  type: string;
  message: string;
  code?: number;
}

export class CamundaApiClient {
  private client: AxiosInstance;
  private authStorageKey: string;

  constructor(config: CamundaApiConfig = {}) {
    const baseURL = config.baseUrl ||
      (import.meta.env.MODE === 'development' ? '/' : '/engine');

    this.authStorageKey = config.authStorageKey ||
      'oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app';

    this.client = axios.create({
      baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem(this.authStorageKey);
          window.location.href = '/';
        }
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private getAuthToken(): string | null {
    try {
      const authDataString = localStorage.getItem(this.authStorageKey);
      if (authDataString) {
        const authData = JSON.parse(authDataString);
        return authData?.access_token || null;
      }
      return null;
    } catch {
      return null;
    }
  }

  private formatError(error: any): CamundaApiError {
    if (error.response?.data) {
      return {
        type: error.response.data.type || 'UnknownError',
        message: error.response.data.message || error.message,
        code: error.response.status,
      };
    }
    return {
      type: 'NetworkError',
      message: error.message || 'Network error occurred',
    };
  }

  // HTTP Methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  // Multipart form data for file uploads
  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Get raw response (for binary data like diagrams)
  async getRaw(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.client.get(url, config);
  }

  // Get blob (for file downloads)
  async getBlob(url: string): Promise<Blob> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Get text (for XML content)
  async getText(url: string): Promise<string> {
    const response = await this.client.get(url, {
      responseType: 'text',
      headers: {
        Accept: 'application/xml, text/xml, */*',
      },
    });
    return response.data;
  }
}

// Singleton instance
let camundaApiClientInstance: CamundaApiClient | null = null;

export function getCamundaApiClient(config?: CamundaApiConfig): CamundaApiClient {
  if (!camundaApiClientInstance) {
    camundaApiClientInstance = new CamundaApiClient(config);
  }
  return camundaApiClientInstance;
}

export function resetCamundaApiClient(): void {
  camundaApiClientInstance = null;
}

export default CamundaApiClient;
